import IAppLogger from '@app/lib/IAppLogger';
import { audioDevicesState } from '@modules/SoftPhoneModule/audioDevicesState';
import { DeviceListChangedPayload } from '@modules/SoftPhoneModule/types';
import { webPhoneEventNames } from '@modules/SoftPhoneModule/constants/events';

/**
 * Handles the `webphone:device-list-changed` event emitted by the webphone MFE
 * when the available hardware devices change (e.g. a device is plugged or unplugged).
 *
 * Updates the available device lists and active device IDs in
 * {@link audioDevicesState}. A hardware disconnect may cause the MFE to
 * fall back to a different active device, which is reflected in the payload.
 */
const handleDeviceListChanged = (
  payload: DeviceListChangedPayload,
  logger: IAppLogger,
): void => {
  const {
    audioInputDevices,
    audioOutputDevices,
    activeAudioInputDeviceId,
    activeAudioOutputDeviceId,
    activeRingOutputDeviceId,
  } = payload;

  logger.debug(webPhoneEventNames.deviceListChanged, {
    inputCount: audioInputDevices.length,
    outputCount: audioOutputDevices.length,
    activeAudioInputDeviceId,
    activeAudioOutputDeviceId,
    activeRingOutputDeviceId,
  });

  audioDevicesState.audioInputDevices = [...audioInputDevices];
  audioDevicesState.audioOutputDevices = [...audioOutputDevices];
  audioDevicesState.currentInputDeviceId = activeAudioInputDeviceId;
  audioDevicesState.currentOutputDeviceId = activeAudioOutputDeviceId;
  audioDevicesState.currentRingDeviceId = activeRingOutputDeviceId;
  audioDevicesState.isRingOutputEnabled =
    activeRingOutputDeviceId !== activeAudioOutputDeviceId;

  // Reset hot-unplug dismiss when a microphone reconnects.
  // The MFE debounces device enumeration events so transient flicker
  // should not reach this handler. If flicker behaviour changes,
  // consider moving this reset to handleDevicesReady instead.
  if (audioInputDevices.length > 0) {
    audioDevicesState.dismissedHotUnplug = false;
  }
};

export default handleDeviceListChanged;
